import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Orders } from "./orders";
import { OrderDetails } from "./order-details";
import { Order } from "@/shared/api/lod-api";
import { clearEventList, setSelectedOrder } from "@/entities/annotation";
import { useAppSelector } from "@/app/appStore";
import { useUrlQueries } from "@/shared/hooks/useUrlQueries";

import { useStaffMeQuery } from "@/shared/api";
import toast from "react-hot-toast";
import { useAnnotationFilters } from "../hooks/useAnnotationFilters";
import { useAnnotationOrdersInfinite } from "../hooks/useAnnotationOrdersInfinite";
import { useNavigate, useParams } from "react-router-dom";
import { SOURCE_OPTIONS } from "@/shared/consts";
import { Flex, Spinner } from "@radix-ui/themes";

interface OrdersContainerProps {
  isOrderLoading: boolean;
  onSelectOrderById: (orderId: string) => void;
  onFinishAnnotation: () => void;
  timerEnabled: boolean;
  timerTimeLeft: number;
  onTimerEnable: (orderId: string) => void;
  onTimerDisable: (orderId: string) => void;
  highlightedOrderId: string | null;
  onCleanupAndNavigateBack: (selectedOrderId?: string) => void;
}

export const OrdersContainer: React.FC<OrdersContainerProps> = ({
  isOrderLoading,
  onSelectOrderById,
  onFinishAnnotation,
  timerEnabled,
  timerTimeLeft,
  onTimerEnable,
  onTimerDisable,
  highlightedOrderId,
  onCleanupAndNavigateBack,
}) => {
  const dispatch = useDispatch();
  const selectedOrder = useAppSelector(
    (state) => state.annotation.selectedOrder
  );
  const { storeKey = "" } = useParams();
  const { getAllQueries } = useUrlQueries();
  const navigate = useNavigate();
  const { data: me } = useStaffMeQuery();

  const { filterState, filterHandlers, activeFilters } = useAnnotationFilters();

  const {
    data: orders,
    isLoading,
    isFetchingMore,
    isError,
    error,
    hasMore,
    fetchMore,
    totalCount,
  } = useAnnotationOrdersInfinite(
    activeFilters,
    filterState.isInitialized && !!me
  );

  useEffect(() => {
    if (isError && error) {
      const errorMessage = isFetchingMore
        ? "Couldn't load more orders. Please check your connection and try again."
        : "Failed to load orders. Please check your connection and try again.";

      toast.error(errorMessage);
    }
  }, [isError, error, isFetchingMore]);

  const handleSelectOrder = (order: Order) => {
    const currentQueries = getAllQueries();
    const queryString = new URLSearchParams(currentQueries).toString();

    navigate({
      pathname: `${order.id}`,
      search: queryString ? `?${queryString}` : "",
    });
  };

  const currentOrderIndex = useMemo(() => {
    if (!orders || !selectedOrder) return -1;
    return orders.findIndex((order) => order.id === selectedOrder.id);
  }, [orders, selectedOrder]);

  const previousOrder = useMemo(() => {
    return orders?.[currentOrderIndex - 1] || null;
  }, [orders, currentOrderIndex]);

  const nextOrder = useMemo(() => {
    return orders?.[currentOrderIndex + 1] || null;
  }, [orders, currentOrderIndex]);

  const handleBack = () => {
    const currentQueries = getAllQueries();
    const queryString = new URLSearchParams(currentQueries).toString();

    navigate({
      pathname: `/stores/${storeKey}`,
      search: queryString ? `?${queryString}` : "",
    });

    onCleanupAndNavigateBack(selectedOrder?.id);
  };

  const handleSwitch = (direction: "previous" | "next") => {
    onCleanupAndNavigateBack();
    dispatch(clearEventList());

    dispatch(
      setSelectedOrder(direction === "previous" ? previousOrder : nextOrder)
    );
  };

  if (isOrderLoading) {
    return (
      <Flex justify="center" align="center" className="h-full">
        <Spinner />
      </Flex>
    );
  }
  return (
    <>
      {selectedOrder ? (
        <OrderDetails
          selectedOrder={selectedOrder}
          previousOrder={previousOrder}
          nextOrder={nextOrder}
          onBack={handleBack}
          onSwitch={handleSwitch}
          onSelectOrder={onSelectOrderById}
          onFinishAnnotation={onFinishAnnotation}
          timerEnabled={timerEnabled}
          timerTimeLeft={timerTimeLeft}
          onTimerEnable={() => onTimerEnable(selectedOrder.id)}
          onTimerDisable={() => onTimerDisable(selectedOrder.id)}
        />
      ) : (
        <Orders
          orders={orders}
          onSelect={handleSelectOrder}
          highlightedOrderId={highlightedOrderId}
          isLoading={isLoading}
          isFetchingMore={isFetchingMore}
          hasMore={hasMore}
          onLoadMore={fetchMore}
          totalCount={totalCount}
          filterState={filterState}
          filterHandlers={filterHandlers}
          sourceList={SOURCE_OPTIONS}
        />
      )}
    </>
  );
};
